// React
import React from 'react';
// External
import { withRouter } from 'react-router-dom';
import {
    Button, Col, Container, Form, Row
} from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { connect } from 'react-redux';
// Image
import spinner from 'assets/icons/spinner.svg';
// Internal
import FileInput from 'components/shared/atoms/FileInput/FileInput';
import getCroppedImg from 'components/shared/molecules/CroppedImage/CroppedImage';
import resizedImage from 'components/shared/molecules/ResizedImage/ResizedImage';
import { updateEngraving } from 'stores/actions';
// Style
import './styles.scss';

class ImageOptions extends React.Component {
    constructor(props) {
        super(props);
        this.cmToPx = 37.795275591; // value of 1 cm = 37.795275591 px
        this.state = {
            errors: [],
            fileUploaded: false,
            fileImgSrc: null,
            isLoading: false,
            selectedPhotoSize: null,
            crop: { x: 0, y: 0 },
            zoom: 1,
            aspect: 4 / 4,
            aspectW: 500,
            aspectH: 500,
            croppedImage: null,
            croppedAreaPixels: null,
            shouldShowCropper: true
        };

        this.showCroppedImage = this.showCroppedImage.bind(this);
        this.onCropComplete = this.onCropComplete.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        const modalTitle = document.getElementsByClassName('modal-title')[0];
        if (typeof modalTitle !== 'undefined') {
            modalTitle.scrollIntoView();
        }
        const navbarBrand = document.getElementsByClassName('navbar-brand')[0];
        if (typeof navbarBrand !== 'undefined') {
            navbarBrand.scrollIntoView();
        }
        this.initialiseStates();
    }

    async handleSubmit() {
        const errors = [];

        if (this.state.croppedImage === null) {
            errors.push('croppedImage');
        }

        await this.setError(errors);

        if (this.hasError('croppedImage')) {
            alert('Please choose an image then press Apply to crop the image so it can be displayed on the right');
            return;
        }

        const engraving = {
            ...this.props.engraving,
            imageOptions: {
                croppedImage: this.state.croppedImage,
                fileImgSrc: this.state.fileImgSrc,
                croppedAreaPixels: this.state.croppedAreaPixels,
                aspectW: this.state.aspectW,
                aspectH: this.state.aspectH
            }
        };

        return errors.length > 0 ? null : engraving;
    }

    onFileChange = (event) => {
        const reader = new window.FileReader();
        reader.readAsDataURL(event.target.files[0]);

        reader.onloadend = function onloadend() {
            this.setState({
                fileImgSrc: [reader.result]
            }, () => {
                this.onFileUpload();
            });
        }.bind(this);
    };

    onFileUpload = async () => {
        this.setState({
            fileUploaded: true,
            shouldShowCropper: true
        });
    }

    onCropChange = (crop) => {
        this.setState({ crop });
    }

    onCropComplete(croppedArea, croppedAreaPixels) {
        this.setState({
            croppedAreaPixels
        });
    }

    onZoomChange = (zoom) => {
        this.setState({ zoom });
    }

    setError(error) {
        this.setState({ errors: error });
    }

    initialiseStates() {
        const selectedVessel = this.props.engraving.productId;
        fetch('../fpc_config.json')
            .then((r) => r.json())
            .then((data) => {
                const selectedConfig = data.Config_File.find((x) => x.SugarID === selectedVessel);
                if (!selectedConfig) {
                    return;
                }
                this.setState({
                    selectedPhotoSize: selectedConfig.PhotoSize
                }, () => {
                    if (this.state.selectedPhotoSize === '0') {
                        alert('Sorry, the selected photo size doesn\'t support image options');
                    } else {
                        let selectedPlaqueWidth;
                        let selectedPlaqueHeight;

                        if (selectedConfig.PhotoSize.indexOf('x') > -1) {
                            const ratioArr = selectedConfig.PhotoSize.split('x');
                            selectedPlaqueWidth = ratioArr[0];
                            selectedPlaqueHeight = ratioArr[1];
                        } else {
                            const ratio = selectedConfig.PhotoSize;
                            selectedPlaqueWidth = ratio;
                            selectedPlaqueHeight = ratio;
                        }

                        const stateList = {
                            aspect: selectedPlaqueWidth / selectedPlaqueHeight,
                            aspectW: selectedPlaqueWidth,
                            aspectH: selectedPlaqueHeight
                        };

                        if (this.props.engraving.imageOptions.fileImgSrc) {
                            stateList.fileImgSrc = this.props.engraving.imageOptions.fileImgSrc;
                        }

                        if (this.props.engraving.imageOptions.croppedImage) {
                            stateList.croppedImage = this.props.engraving.imageOptions.croppedImage;
                        }

                        if (this.props.engraving.imageOptions.croppedAreaPixels) {
                            stateList.croppedAreaPixels = this.props.engraving.imageOptions.croppedAreaPixels;
                        }

                        if (this.props.engraving.imageOptions.aspectW) {
                            stateList.aspectW = this.props.engraving.imageOptions.aspectW;
                        }

                        if (this.props.engraving.imageOptions.aspectH) {
                            stateList.aspectH = this.props.engraving.imageOptions.aspectH;
                        }

                        this.setState(stateList, () => {
                            this.onFileUpload();
                        });
                    }
                });
            });
    }

    hasError(fieldError) {
        return this.state.errors.indexOf(fieldError) >= 0;
    }

    async showCroppedImage() {
        const { aspectW, aspectH } = this.state;

        this.setState({
            isLoading: true
        });

        // simulate file upload process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const croppedImage = await getCroppedImg(
            this.state.fileImgSrc,
            this.state.croppedAreaPixels,
            0
        );

        const croppedAndResizedImage = await resizedImage(croppedImage,
            aspectW * this.cmToPx, aspectH * this.cmToPx);

        this.setState({
            croppedImage: croppedAndResizedImage,
            isLoading: false,
            shouldShowCropper: false
        }, () => {
            this.setError([]);
        });
    }

    render() {
        return (
            <Container fluid className="engraving-container">
                <Row>
                    <Col lg={7} className="col-12">
                        <div className="section-description">
                            <p>
                                This item allows for the addition of a single image to be incorporated
                                with
                                the artwork. For optimal image quality, larger files are preferred, up
                                to a
                                maximum file size of 20MB. Ensure the image is either a JPEG, GIF, PNG,
                                TIFF
                                or BMP file.
                            </p>
                        </div>

                        <div className="engraving-options-wrapper">
                            <Form className="image-options-form">
                                <Form.Row>
                                    <Form.Group as={Col} className="file-input-container">
                                        <Form.Label>SELECT A FILE</Form.Label>
                                        <FileInput
                                            name="imageOptions"
                                            className="file-input"
                                            onChange={this.onFileChange}
                                            disabled={this.state.selectedPhotoSize === '0'}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} className="upload-button-container">
                                        {/* eslint-disable-next-line no-nested-ternary */}
                                        {!this.state.isLoading ? (
                                            (this.state.fileImgSrc) ? (
                                                <Button
                                                    className="btn-outline btn-apply"
                                                    onClick={this.showCroppedImage}
                                                    disabled={
                                                        this.state.selectedPhotoSize === '0'
                                                    }
                                                    style={{ maxWidth: '200px' }}
                                                >
                                                    Apply
                                                </Button>
                                            ) : null
                                        ) : (
                                            <img
                                                className="loading-icon"
                                                alt="loading-icon"
                                                src={spinner}
                                            />
                                        )}
                                    </Form.Group>
                                </Form.Row>
                            </Form>
                        </div>

                        <div>
                            {
                                this.state.shouldShowCropper && this.state.fileImgSrc ? (
                                    <div className="cropper-container" style={{ maxWidth: '100%' }}>
                                        {this.state.fileUploaded ? (
                                            <Cropper
                                                image={this.state.fileImgSrc}
                                                crop={this.state.crop}
                                                zoom={this.state.zoom}
                                                aspect={this.state.aspect}
                                                onCropChange={this.onCropChange}
                                                onCropComplete={this.onCropComplete}
                                                onZoomChange={this.onZoomChange}
                                            />
                                        ) : null}
                                    </div>
                                ) : null
                            }
                        </div>
                    </Col>
                    <Col lg={5} className="col-12">
                        {(this.state.croppedImage) ? (
                            <div>
                                <div className="section-description">
                                    <p>Cropped Image:</p>
                                </div>
                                <img
                                    src={this.state.croppedImage}
                                    alt="Crop Result"
                                    className="crop-result-img"
                                    style={{ maxWidth: '100%' }}
                                />
                            </div>
                        ) : null}
                    </Col>
                </Row>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    engraving: state.engraving
});

const mapDispatchToProps = (dispatch) => {
    return {
        updateEngraving: (engravingObj) => dispatch(updateEngraving(engravingObj))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ImageOptions));
